import { useState, useEffect } from "react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Users, Home, TrendingUp, DollarSign, Award, Building, Wrench } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Loading, StatCard, SectionCard } from "../components/kit";
import { money } from "../lib/constants";

const COLORS = ["#D4AF37", "#1E3A8A", "#0EA5E9", "#10B981", "#8B5CF6", "#EC4899", "#F97316", "#64748B"];

export default function Reports() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/reports").then(({ data }) => setR(data)); }, []);
  if (!r) return <Loading label="Building reports" />;
  const s = r.summary;

  const Chart = ({ title, data, type = "bar" }) => (
    <SectionCard title={title}>
      {(!data || data.length === 0) ? <p className="text-sm text-slate-400 py-8 text-center">No data</p> : type === "bar" ? (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 30 }}>
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-25} textAnchor="end" height={50} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
            <Tooltip cursor={{ fill: "rgba(212,175,55,0.08)" }} />
            <Bar dataKey="value" radius={[6, 6, 0, 0]}>{data.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <PieChart><Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45} paddingAngle={2}>{data.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
        </ResponsiveContainer>
      )}
    </SectionCard>
  );

  return (
    <div className="animate-fade-up">
      <PageHeader title="Reports & Analytics" subtitle="Business intelligence across leads, properties, pipeline, and closings. Professionals & vendors are reported separately — never counted as leads." testid="reports-header" />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-4 mb-6">
        <StatCard label="Clients & Leads" value={s.total_contacts} icon={Users} testid="report-contacts" />
        <StatCard label="Professionals & Vendors" value={s.total_professionals} icon={Wrench} accent="blue" testid="report-professionals" />
        <StatCard label="Properties" value={s.total_properties} icon={Building} accent="gold" />
        <StatCard label="Active Buyers" value={s.active_buyers} icon={TrendingUp} accent="blue" />
        <StatCard label="Active Sellers" value={s.active_sellers} icon={Home} accent="green" />
        <StatCard label="Pipeline Value" value={money(s.pipeline_value)} icon={DollarSign} accent="green" />
        <StatCard label="Closed Volume" value={money(s.closed_volume)} icon={Award} accent="gold" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Chart title="Leads by Source" data={r.leads_by_source} type="pie" />
        <Chart title="Leads by City" data={r.leads_by_city} />
        <Chart title="Leads by Temperature" data={r.leads_by_temperature} type="pie" />
        <Chart title="Pipeline by Stage" data={r.pipeline_by_stage} />
        <Chart title="Properties by Status" data={r.properties_by_status} type="pie" />
        <Chart title="Property Price Ranges" data={r.property_price_ranges} />
        <Chart title="Professionals & Vendors by Category" data={r.professionals_by_category} />
        <Chart title="Professionals & Vendors by City" data={r.professionals_by_city} type="pie" />
      </div>
    </div>
  );
}
